import SessionStore from '../../State/SessionStore'

/**
 * STANDARDIZED BILLING CALCULATION UTILITIES
 * Following PRD rules implemented in server_node/src/utils/stripe.ts
 *
 * PRD Rules:
 * - Monthly: Flat per-user fee, no default discounts, remove coupons when switching from yearly
 * - Yearly: Flat per-user fee + 15% discount, preserve better existing coupons
 * - Payment format: Date → Amount Due → Discounts → Adjustments → Tax → Credit → Total/Credit Applied
 */

// Import plan and coupon lookup data (matching OrganisationSubscriptionModel.js)
const planLookup = {
    '1_Month_Aus_Admin': {
        id: '1_Month_Aus_Admin',
        interval: 'month',
        intervalCount: 1,
        amount: 4500,
        currency: 'aud',
    },
    '1_Month_Aus_Standard': {
        id: '1',
        interval: 'month',
        intervalCount: 1,
        amount: 2000,
        currency: 'aud',
    },
    '12_Month_Aus_Admin': {
        id: '12_Month_Aus_Admin',
        interval: 'year',
        intervalCount: 1,
        amount: 54000,
        currency: 'aud',
    },
    '12_Month_Aus_Standard': {
        id: '12_Month_Aus_Standard',
        interval: 'year',
        intervalCount: 1,
        amount: 24000,
        currency: 'aud',
    },
    '6_Month_Aus_Admin': {
        id: '6_Month_Aus_Admin',
        interval: 'month',
        intervalCount: 6,
        amount: 27000,
        currency: 'aud',
    },
    '6_Month_Aus_Standard': {
        id: '6_Month_Aus_Standard',
        interval: 'month',
        intervalCount: 6,
        amount: 12000,
        currency: 'aud',
    },
    '1_Month_Aus_Standard_$15': {
        id: '15AUD_User',
        interval: 'month',
        intervalCount: 1,
        amount: 1500,
        currency: 'aud',
    },
    '12_Month_Aus_Standard_$15': {
        id: '12_Month_15AUD',
        interval: 'year',
        intervalCount: 1,
        amount: 18000,
        currency: 'aud',
    },
    '12_Month_Aus_Standard_BS': {
        id: '12_Month_Timesheet_BS',
        interval: 'year',
        intervalCount: 1,
        amount: 18000,
        currency: 'aud',
    },
    '1_Month_US_Admin': {
        id: '1month_admin_usd',
        interval: 'month',
        intervalCount: 1,
        amount: 3500,
        currency: 'usd',
    },
    '1_Month_US_Standard': {
        id: '1month_timesheet_usd',
        interval: 'month',
        intervalCount: 1,
        amount: 1500,
        currency: 'usd',
    },
    '12_Month_US_Admin': {
        id: '12month_admin_usd',
        interval: 'year',
        intervalCount: 1,
        amount: 42000,
        currency: 'usd',
    },
    '12_Month_US_Standard': {
        id: '12month_timesheet_usd',
        interval: 'year',
        intervalCount: 1,
        amount: 18000,
        currency: 'usd',
    },
    '1_Month_US_Any_$23': {
        id: '1_monthly_plan',
        interval: 'month',
        intervalCount: 1,
        amount: 2300,
        currency: 'usd',
    },
    '12_Month_US_Admin_$30': {
        id: '12month_admin_30usd',
        interval: 'year',
        intervalCount: 1,
        amount: 36000,
        currency: 'usd',
    },
    '1_Month_Aus_29': {
        id: 'price_1MnYUZC5Z2UupQFYupQNBHo6',
        interval: 'month',
        intervalCount: 1,
        amount: 2900,
        currency: 'aud',
    },
    '12_Month_Aus_29': {
        id: 'price_1MnYV3C5Z2UupQFYEBt85US1',
        interval: 'year',
        intervalCount: 1,
        amount: 34800,
        currency: 'aud',
    },
    '1_Month_US_19': {
        id: 'price_1MnYW7C5Z2UupQFYHpzWaEih',
        interval: 'month',
        intervalCount: 1,
        amount: 1900,
        currency: 'usd',
    },
    '12_Month_US_19': {
        id: 'price_1MnYWeC5Z2UupQFYmgDbRJqy',
        interval: 'year',
        intervalCount: 1,
        amount: 22800,
        currency: 'usd',
    },
    '1_Month_Aus_35': {
        id: 'price_1Q8B6xC5Z2UupQFYHil12VMM',
        interval: 'month',
        intervalCount: 1,
        amount: 3500,
        currency: 'aud',
    },
    '12_Month_Aus_35': {
        id: 'price_1Q8B7tC5Z2UupQFY5PjrScn4',
        interval: 'year',
        intervalCount: 1,
        amount: 42000,
        currency: 'aud',
    },
    '1_Month_US_25': {
        id: 'price_1Q8B8tC5Z2UupQFYLobMM9KB',
        interval: 'month',
        intervalCount: 1,
        amount: 2500,
        currency: 'usd',
    },
    '12_Month_US_25': {
        id: 'price_1Q8B9RC5Z2UupQFYCVOeNrd7',
        interval: 'year',
        intervalCount: 1,
        amount: 30000,
        currency: 'usd',
    },
}

const couponLookup = {
    '10_percent_forever': { id: '10_percent_forever', percentDiscount: 10 },
    '15_percent_forever': { id: '15_percent_forever', percentDiscount: 15 },
    '20_percent_forever': { id: '20_percent_forever', percentDiscount: 20 },
    '25_percent_forever': { id: '25_percent_forever', percentDiscount: 25 },
    '35_percent_forever': { id: '35_percent_forever', percentDiscount: 35 },
    '50_percent_forever': { id: '50_percent_forever', percentDiscount: 50 },
    FREE: { id: 'FREE', percentDiscount: 100 },
    '20_percent_1_year': { id: '20_percent_1_year', percentDiscount: 20 },
    '25_percent_1_year': { id: '25_percent_1_year', percentDiscount: 25 },
    '50_percent_12months': { id: '50_percent_12months', percentDiscount: 50 },
    '35_percent_off_6_months': {
        id: '35_percent_off_6_months',
        percentDiscount: 35,
    },
    '5_percent_discount_once': {
        id: '5_percent_discount_once',
        percentDiscount: 5,
    },
    '10_percent_discount_once': {
        id: '10_percent_discount_once',
        percentDiscount: 10,
    },
    '20_percent_discount_once': {
        id: '20_percent_discount_once',
        percentDiscount: 20,
    },
    '25_percent_discount_once': {
        id: '25_percent_discount_once',
        percentDiscount: 25,
    },
    '35_percent_discount_once': {
        id: '35_percent_discount_once',
        percentDiscount: 35,
    },
    '50_percent_discount_once': {
        id: '50_percent_discount_once',
        percentDiscount: 50,
    },
}

const taxLookup = {
    au: { name: 'GST', taxPercent: 10 },
}

/**
 * Standardized coupon logic following PRD rules
 * Matches server-side implementation in stripe.ts
 */

/**
 * Determine the appropriate coupon for monthly billing according to PRD rules
 * Rules: No default discounts, preserve existing coupons only if already applied, remove all coupons when switching from yearly
 */
export const getStandardizedMonthlyCoupon = (
    currentPeriod,
    existingMonthlyCouponId,
    existingYearlyCouponId,
    couponLookupToUse = couponLookup
) => {
    // Rule: When switching from yearly to monthly, remove all existing coupons
    if (currentPeriod === 'yearly') {
        return {
            id: '',
            percentDiscount: 0,
            shouldApply: false,
            reason: 'PRD Rule: Remove all coupons when switching from yearly to monthly',
        }
    }

    // Rule: For monthly billing, only preserve existing monthly coupons (no default discounts)
    if (existingMonthlyCouponId && couponLookupToUse[existingMonthlyCouponId]) {
        const couponInfo = couponLookupToUse[existingMonthlyCouponId]
        return {
            id: existingMonthlyCouponId,
            percentDiscount: couponInfo.percentDiscount,
            shouldApply: true,
            reason: 'PRD Rule: Preserve existing monthly coupon',
        }
    }

    // Rule: No default discounts for monthly billing
    return {
        id: '',
        percentDiscount: 0,
        shouldApply: false,
        reason: 'PRD Rule: No default discounts for monthly billing',
    }
}

/**
 * Determine the appropriate coupon for yearly billing according to PRD rules
 * Rules: Apply 15% discount by default, preserve existing better coupons when switching from monthly
 */
export const getStandardizedYearlyCoupon = (
    currentPeriod,
    existingMonthlyCouponId,
    existingYearlyCouponId,
    couponLookupToUse = couponLookup
) => {
    // Rule: When switching from monthly to yearly, compare existing monthly coupon with 15% yearly discount
    if (
        currentPeriod === 'monthly' &&
        existingMonthlyCouponId &&
        couponLookupToUse[existingMonthlyCouponId]
    ) {
        const existingCouponInfo = couponLookupToUse[existingMonthlyCouponId]

        if (existingCouponInfo.percentDiscount > 15) {
            return {
                id: existingMonthlyCouponId,
                percentDiscount: existingCouponInfo.percentDiscount,
                shouldApply: true,
                reason: "PRD Rule: Preserve existing monthly coupon that's better than 15% yearly discount",
            }
        }
    }

    // Rule: For yearly billing, apply 15% discount by default (or preserve existing yearly coupon if better)
    if (existingYearlyCouponId && couponLookupToUse[existingYearlyCouponId]) {
        const existingCouponInfo = couponLookupToUse[existingYearlyCouponId]

        if (existingCouponInfo.percentDiscount >= 15) {
            return {
                id: existingYearlyCouponId,
                percentDiscount: existingCouponInfo.percentDiscount,
                shouldApply: true,
                reason: 'PRD Rule: Preserve existing yearly coupon that meets minimum 15%',
            }
        }
    }

    // Rule: Apply default 15% yearly discount
    return {
        id: '15_percent_forever',
        percentDiscount: 15,
        shouldApply: true,
        reason: 'PRD Rule: Apply default 15% yearly discount',
    }
}

/**
 * Calculate standardized fee structure (flat per-user rate for both admin/standard users)
 * Legacy subscriptions maintain tiered pricing but follow same coupon rules
 */
export const calculateStandardizedFees = (
    organisationCountry,
    period,
    subscribedAt,
    staffList,
    planLookupToUse = planLookup
) => {
    // Determine if this is a legacy subscription
    const subscribedDate = subscribedAt ? new Date(subscribedAt) : null
    const isLegacy = subscribedDate && subscribedDate < new Date('2023-03-20')
    const isAuNz = ['au', 'nz'].includes(organisationCountry)

    // Get staff totals
    let numAdminUsers = 0
    let numStandardUsers = 0

    staffList.forEach((staffMember) => {
        const billingLevel = getBillingLevel(staffMember)
        if (billingLevel === 'admin') {
            numAdminUsers++
        } else if (billingLevel === 'standard') {
            numStandardUsers++
        }
    })

    const totalUsers = numAdminUsers + numStandardUsers
    const currency = isAuNz ? 'aud' : 'usd'

    if (isLegacy) {
        // Legacy subscriptions maintain separate admin/standard pricing
        const adminPriceKey =
            period === 'yearly'
                ? isAuNz
                    ? '12_Month_Aus_Admin'
                    : '12_Month_US_Admin'
                : isAuNz
                  ? '1_Month_Aus_Admin'
                  : '1_Month_US_Admin'

        const standardPriceKey =
            period === 'yearly'
                ? isAuNz
                    ? '12_Month_Aus_Standard'
                    : '12_Month_US_Standard'
                : isAuNz
                  ? '1_Month_Aus_Standard'
                  : '1_Month_US_Standard'

        const adminPrice = planLookupToUse[adminPriceKey]?.amount || 0
        const standardPrice = planLookupToUse[standardPriceKey]?.amount || 0

        const amountDue =
            (numAdminUsers * adminPrice + numStandardUsers * standardPrice) /
            100
        const avgPricePerUser = totalUsers > 0 ? amountDue / totalUsers : 0

        return {
            amountDue,
            userCount: totalUsers,
            pricePerUser: avgPricePerUser,
            currency,
        }
    } else {
        // Modern subscriptions use flat per-user pricing
        const flatPriceKey =
            period === 'yearly'
                ? isAuNz
                    ? '12_Month_Aus_35'
                    : '12_Month_US_25'
                : isAuNz
                  ? '1_Month_Aus_35'
                  : '1_Month_US_25'

        const flatPrice = planLookupToUse[flatPriceKey]?.amount || 0
        const amountDue = (totalUsers * flatPrice) / 100
        const pricePerUser = flatPrice / 100

        return {
            amountDue,
            userCount: totalUsers,
            pricePerUser,
            currency,
        }
    }
}

/**
 * Create standardized payment breakdown following PRD format
 * Format: Date → Amount Due → Discounts → Adjustments → Tax → Credit → Total/Credit Applied
 */
export const createStandardizedPaymentBreakdown = (
    amountDue,
    couponInfo,
    adjustments = 0,
    credit = 0,
    organisationCountry = 'au',
    nextPaymentDate = null,
    currency = 'usd',
    taxLookupToUse = taxLookup
) => {
    // Calculate discount (always subtract from amount due)
    const discount = couponInfo.shouldApply
        ? (amountDue * couponInfo.percentDiscount) / 100
        : 0

    // Calculate tax (applied after discounts and adjustments)
    const taxPercent = taxLookupToUse[organisationCountry]?.taxPercent || 0
    const taxName = taxLookupToUse[organisationCountry]?.name || 'Tax'
    const taxableAmount = amountDue - discount + adjustments
    const tax = Math.max(0, taxableAmount * (taxPercent / 100))

    // Calculate subtotal (before credit application)
    const subtotal = amountDue - discount + adjustments + tax

    // Calculate final amounts according to PRD rules
    let grandTotal = 0
    let creditApplied = 0

    if (subtotal <= credit) {
        // Credit covers entire amount - show "Credit Applied" instead of total
        grandTotal = 0
        creditApplied = subtotal
    } else {
        // Credit partially covers or doesn't cover - show remaining total
        grandTotal = subtotal - credit
        creditApplied = 0
    }

    const breakdown = {
        nextPaymentDate,
        amountDue,
        discount,
        adjustments,
        tax,
        credit,
        grandTotal,
        creditApplied,
        taxName,
        currency: currency.toUpperCase(),
    }

    return breakdown
}

/**
 * Get default plan IDs based on organisation subscription details
 * Replicates logic from OrganisationSubscriptionModel.js
 */
export const getDefaultPlanIds = (
    period,
    subscribedAt,
    organisationCountry
) => {
    const subscribedDate = subscribedAt ? new Date(subscribedAt) : null
    const isLegacy = subscribedDate && subscribedDate < new Date('2023-03-20')
    const isAuNz = ['au', 'nz'].includes(organisationCountry)

    if (period === 'monthly') {
        if (isLegacy) {
            return {
                adminPlanId: isAuNz ? '1_Month_Aus_Admin' : '1_Month_US_Admin',
                standardPlanId: isAuNz
                    ? '1_Month_Aus_Standard'
                    : '1_Month_US_Standard',
            }
        }
        return {
            adminPlanId: isAuNz ? '1_Month_Aus_35' : '1_Month_US_25',
            standardPlanId: isAuNz ? '1_Month_Aus_35' : '1_Month_US_25',
        }
    } else {
        // yearly
        if (isLegacy) {
            return {
                adminPlanId: isAuNz
                    ? '12_Month_Aus_Admin'
                    : '12_Month_US_Admin',
                standardPlanId: isAuNz
                    ? '12_Month_Aus_Standard'
                    : '12_Month_US_Standard',
            }
        }
        return {
            adminPlanId: isAuNz ? '12_Month_Aus_35' : '12_Month_US_25',
            standardPlanId: isAuNz ? '12_Month_Aus_35' : '12_Month_US_25',
        }
    }
}

/**
 * Get the correct fee for a staff member based on their billing level and subscription plan
 * This replaces hardcoded values with actual plan pricing from the plan lookup
 */
export const getStaffMemberFee = (
    billingLevel,
    period,
    isLegacySubscription,
    organisationCountry,
    subscribedAt,
    organisationSubscription,
    planLookupToUse = planLookup
) => {
    // For non-billable staff
    if (billingLevel === 'none') {
        return 0
    }

    if (isLegacySubscription) {
        // Legacy subscriptions have separate admin/standard pricing
        const planType = billingLevel === 'admin' ? 'admin' : 'standard'
        const planKey =
            period === 'yearly'
                ? organisationSubscription?.[
                      `yearly${planType === 'admin' ? 'Admin' : 'Standard'}PlanId`
                  ] ||
                  getDefaultPlanIds(period, subscribedAt, organisationCountry)[
                      `${planType}PlanId`
                  ]
                : organisationSubscription?.[
                      `monthly${planType === 'admin' ? 'Admin' : 'Standard'}PlanId`
                  ] ||
                  getDefaultPlanIds(period, subscribedAt, organisationCountry)[
                      `${planType}PlanId`
                  ]

        const planPrice = planLookupToUse[planKey]?.amount || 0
        return planPrice / 100 // Convert cents to dollars
    } else {
        // Modern subscriptions use flat per-user pricing
        const isAuNz = ['au', 'nz'].includes(organisationCountry)
        const flatPriceKey =
            period === 'yearly'
                ? isAuNz
                    ? '12_Month_Aus_35'
                    : '12_Month_US_25'
                : isAuNz
                  ? '1_Month_Aus_35'
                  : '1_Month_US_25'

        const flatPrice = planLookupToUse[flatPriceKey]?.amount || 0
        return flatPrice / 100 // Convert cents to dollars
    }
}

/**
 * Determine staff billing level based on permissions and overrides
 * Replicates logic from server-side getBillingLevel function
 */
export const getBillingLevel = (staffMember) => {
    if (staffMember.billingLevelOverride) {
        return staffMember.billingLevelOverride
    }

    if (!staffMember.hasLogin || !staffMember.email || staffMember.isArchived) {
        return 'none'
    }

    if (staffMember.permissions) {
        let permissions = {}
        if (typeof staffMember.permissions === 'string') {
            try {
                permissions = JSON.parse(staffMember.permissions)
            } catch {
                return 'standard'
            }
        } else {
            permissions = staffMember.permissions || {}
        }

        // Determine admin status using consistent logic (matches server-side isAdmin function)
        // Admin criteria: can see salaries OR operational expenses OR billing OR settings
        const canSeeSalaries =
            (permissions.staffPermissions === 'view' ||
                permissions.staffPermissions === 'edit' ||
                permissions.staffPermissions === 'admin') &&
            (permissions.payRate === 'view' ||
                permissions.payRate === 'edit' ||
                permissions.payRate === 'admin' ||
                permissions.overtimeRate === 'view' ||
                permissions.overtimeRate === 'edit' ||
                permissions.overtimeRate === 'admin')
        const canSeeOperationalExpenses =
            permissions.operationalExpenses === 'view' ||
            permissions.operationalExpenses === 'edit' ||
            permissions.operationalExpenses === 'admin'
        const canViewBilling = permissions.billing === 'edit'
        const canViewSettings =
            permissions.organisationSettings === 'view' ||
            permissions.organisationSettings === 'edit' ||
            permissions.organisationSettings === 'admin'

        const hasAdminPermission =
            canSeeSalaries ||
            canSeeOperationalExpenses ||
            canViewBilling ||
            canViewSettings

        return hasAdminPermission ? 'admin' : 'standard'
    }

    return 'standard'
}

/**
 * Calculate proration adjustments for billing period changes
 * Estimates what Stripe would charge/credit for the period change
 */
export const calculateProrationAdjustments = ({
    currentBillingData,
    newSubtotal,
    subscriptionPeriod,
    organisationSubscription,
}) => {
    if (!currentBillingData || !currentBillingData.nextPaymentDate) {
        return 0
    }

    const currentPeriod = organisationSubscription?.subscriptionPeriod
    const nextPaymentDate = new Date(currentBillingData.nextPaymentDate)
    const today = new Date()

    // Calculate days remaining in current billing cycle
    const daysRemaining = Math.max(
        0,
        Math.floor((nextPaymentDate - today) / (1000 * 60 * 60 * 24))
    )

    // Get current period length in days
    const currentPeriodDays = currentPeriod === 'yearly' ? 365 : 30

    // Calculate proration factor (how much of current period is unused)
    const prorationFactor = daysRemaining / currentPeriodDays

    // Calculate current period cost that would be credited
    const currentAmountDue = currentBillingData.amountDue || 0
    const currentCredit = currentAmountDue * prorationFactor

    // Calculate immediate charge for new period
    let immediateCharge = 0
    if (subscriptionPeriod === 'yearly' && currentPeriod === 'monthly') {
        // Switching from monthly to yearly - charge for remaining days at new rate
        const yearlyDailyRate = newSubtotal / 365
        immediateCharge = yearlyDailyRate * daysRemaining
    } else if (subscriptionPeriod === 'monthly' && currentPeriod === 'yearly') {
        // Switching from yearly to monthly - charge for remaining days at new rate
        const monthlyDailyRate = newSubtotal / 30
        immediateCharge = monthlyDailyRate * Math.min(daysRemaining, 30)
    }

    // Net adjustment is immediate charge minus credit from unused current period
    const netAdjustment = immediateCharge - currentCredit

    return Math.round(netAdjustment * 100) / 100 // Round to 2 decimal places
}

/**
 * Calculate billing preview for a given staff list and billing period
 * @param {Array} staffList - List of staff members
 * @param {'monthly' | 'yearly'} billingPeriod - Target billing period
 * @param {Object} subscription - Current subscription data
 * @param {Object} planLookupData - Plan pricing lookup table from server
 * @param {Object} couponLookupData - Coupon discount lookup table from server
 * @param {Object} taxLookupData - Tax rate lookup table from server
 * @returns {Object} Preview calculation results
 */
export function calculateBillingPreview(
    staffList,
    billingPeriod,
    subscription,
    planLookupData,
    couponLookupData,
    taxLookupData
) {
    // Validate inputs
    if (!staffList || !billingPeriod) {
        throw new Error(
            'Missing required parameters for billing preview calculation'
        )
    }

    // Use server-provided lookup data if available, fallback to local data
    const activePlanLookup =
        planLookupData && Object.keys(planLookupData).length > 0
            ? planLookupData
            : planLookup
    const activeCouponLookup =
        couponLookupData && Object.keys(couponLookupData).length > 0
            ? couponLookupData
            : couponLookup
    const activeTaxLookup =
        taxLookupData && Object.keys(taxLookupData).length > 0
            ? taxLookupData
            : taxLookup

    // Get organisation country from session or subscription
    const organisationCountry =
        SessionStore.organisation?.country ||
        subscription?.organisation?.country ||
        SessionStore.user?.country ||
        'au' // Default fallback

    return calculateBillingPreviewInternal({
        staffList,
        subscriptionPeriod: billingPeriod,
        organisationSubscription: subscription,
        organisationCountry,
        isNewSubscription: !subscription?.hasSubscription,
        currentBillingData: null,
        planLookup: activePlanLookup,
        couponLookup: activeCouponLookup,
        taxLookup: activeTaxLookup,
    })
}

/**
 * Internal calculation function with object parameters - STANDARDIZED VERSION
 * Calculate billing preview following PRD rules implemented in server-side stripe.ts
 *
 * IMPORTANT: This now follows the exact same logic as generateStandardizedBillingPreview()
 * in server_node/src/utils/stripe.ts to ensure client-server consistency
 */
export const calculateBillingPreviewInternal = ({
    staffList,
    subscriptionPeriod,
    organisationSubscription,
    organisationCountry,
    isNewSubscription = false,
    currentBillingData = null,
    planLookup: planLookupToUse = planLookup,
    couponLookup: couponLookupToUse = couponLookup,
    taxLookup: taxLookupToUse = taxLookup,
}) => {
    // Calculate standardized fees using the new standardized logic
    const feeData = calculateStandardizedFees(
        organisationCountry,
        subscriptionPeriod,
        organisationSubscription?.subscribedAt,
        staffList,
        planLookupToUse
    )

    // Apply standardized coupon logic
    const currentPeriod = isNewSubscription
        ? undefined
        : organisationSubscription?.subscriptionPeriod
    let couponInfo

    if (subscriptionPeriod === 'monthly') {
        couponInfo = getStandardizedMonthlyCoupon(
            currentPeriod,
            organisationSubscription?.monthlyCouponId,
            organisationSubscription?.yearlyCouponId,
            couponLookupToUse
        )
    } else {
        couponInfo = getStandardizedYearlyCoupon(
            currentPeriod,
            organisationSubscription?.monthlyCouponId,
            organisationSubscription?.yearlyCouponId,
            couponLookupToUse
        )
    }

    // Get current credit balance
    const credit = isNewSubscription ? 0 : currentBillingData?.credit || 0

    // Calculate adjustments (for period changes, this would include prorations)
    let adjustments = 0
    if (!isNewSubscription && currentBillingData) {
        adjustments = calculateProrationAdjustments({
            currentBillingData,
            newSubtotal: feeData.amountDue,
            subscriptionPeriod,
            organisationSubscription,
        })
    }

    // Create standardized payment breakdown
    const nextPaymentDate = isNewSubscription
        ? new Date()
        : currentBillingData?.nextPaymentDate || new Date()

    const breakdown = createStandardizedPaymentBreakdown(
        feeData.amountDue,
        couponInfo,
        adjustments,
        credit,
        organisationCountry,
        nextPaymentDate,
        feeData.currency,
        taxLookupToUse
    )

    // Get staff breakdown for display - using standardized fee calculation
    const subscribedDate = organisationSubscription?.subscribedAt
        ? new Date(organisationSubscription.subscribedAt)
        : null
    const isLegacy = subscribedDate && subscribedDate < new Date('2023-03-20')

    const staffBreakdown = staffList.map((staff, index) => {
        const billingLevel = getBillingLevel(staff)
        const isBillable = billingLevel !== 'none'

        return {
            index: index + 1,
            name: `${staff.firstName || ''} ${staff.lastName || ''}`.trim(),
            billingLevel,
            fee: getStaffMemberFee(
                billingLevel,
                subscriptionPeriod,
                isLegacy,
                organisationCountry,
                subscribedDate,
                organisationSubscription,
                planLookupToUse
            ),
            isBillable,
            staff: staff,
        }
    })

    const result = {
        ...breakdown,
        staffBreakdown,
        couponInfo,
        userCount: feeData.userCount,
        pricePerUser: feeData.pricePerUser,
        // Additional breakdown info for backward compatibility
        subtotal: feeData.amountDue,
        numAdminUsers: staffBreakdown.filter(
            (s) => s.billingLevel === 'admin' && s.isBillable
        ).length,
        numStandardUsers: staffBreakdown.filter(
            (s) => s.billingLevel === 'standard' && s.isBillable
        ).length,
    }

    // Validate result follows standardized rules
    const validation = validateStandardizedCalculation(
        result,
        subscriptionPeriod
    )

    if (!validation.isValid) {
        throw new Error(
            `Billing calculation validation failed: ${validation.errors.join(', ')}`
        )
    }

    return result
}

/**
 * Calculate preview for billing period change
 * Uses current billing data and calculates what the new period would cost
 */
export const calculateBillingPeriodChangePreview = ({
    staffList,
    newPeriod,
    organisationSubscription,
    organisationCountry,
    currentBillingData,
}) => {
    return calculateBillingPreview({
        staffList,
        subscriptionPeriod: newPeriod,
        organisationSubscription,
        organisationCountry,
        isNewSubscription: false,
        currentBillingData,
    })
}

/**
 * Calculate preview for new subscription
 */
export const calculateNewSubscriptionPreview = ({
    staffList,
    subscriptionPeriod,
    organisationCountry,
    organisationSubscription = null,
}) => {
    return calculateBillingPreview({
        staffList,
        subscriptionPeriod,
        organisationSubscription,
        organisationCountry,
        isNewSubscription: true,
    })
}

/**
 * Validate that payment calculations follow standardized PRD rules
 * @param {Object} result - Billing calculation result
 * @param {string} period - Billing period ('monthly' | 'yearly')
 * @returns {Object} Validation result with errors if any
 */
export function validateStandardizedCalculation(result, period) {
    const errors = []
    const warnings = []

    // Validate payment display format order
    const requiredFields = [
        'amountDue',
        'discount',
        'tax',
        'credit',
        'grandTotal',
    ]
    for (const field of requiredFields) {
        if (result[field] === undefined || result[field] === null) {
            errors.push(`Missing required field: ${field}`)
        }
    }

    // Validate coupon logic according to PRD rules
    if (period === 'monthly') {
        // Monthly should not have default discounts unless specifically preserved
        if (
            result.discount > 0 &&
            result.couponInfo &&
            result.couponInfo.reason &&
            !result.couponInfo.reason.includes('preserved')
        ) {
            warnings.push(
                `Monthly billing has discount but may not follow PRD rules: ${result.couponInfo.reason}`
            )
        }
    } else if (period === 'yearly') {
        // Yearly should have 15% discount unless a better coupon is preserved
        if (
            result.discount === 0 &&
            (!result.couponInfo || !result.couponInfo.reason.includes('better'))
        ) {
            warnings.push('Yearly billing missing expected 15% discount')
        }
    }

    // Validate Total vs Credit Applied logic
    const shouldApplyCredit =
        result.credit > 0 &&
        result.credit >=
            result.amountDue +
                result.tax +
                (result.adjustments || 0) -
                result.discount
    const hasZeroTotal = result.grandTotal === 0

    if (shouldApplyCredit && !hasZeroTotal) {
        errors.push(
            'Credit should cover entire amount but grandTotal is not zero'
        )
    }
    if (!shouldApplyCredit && hasZeroTotal && result.credit > 0) {
        warnings.push(
            'GrandTotal is zero but credit does not cover full amount'
        )
    }

    // Validate tax calculation percentage
    const subtotalAfterDiscounts =
        result.amountDue + (result.adjustments || 0) - result.discount
    if (subtotalAfterDiscounts > 0 && result.tax > 0) {
        const taxPercentage = (result.tax / subtotalAfterDiscounts) * 100
        if (taxPercentage < 0 || taxPercentage > 25) {
            warnings.push(
                `Tax percentage (${taxPercentage.toFixed(1)}%) seems unusual`
            )
        }
    }

    return {
        isValid: errors.length === 0,
        errors,
        warnings,
    }
}

export { planLookup, couponLookup, taxLookup }
